import React, { useEffect, useState, useCallback } from 'react'
import Image from 'next/image'

const LazyImage = ({ src, alt, width = null, height = null, ...props }) => {
  const sizes = '(max-width: 1200px) 100vw, 50vw'
  const [base64BlurDataURL, setBase64BlurDataURL] = useState('')
  const [imageError, setImageError] = useState(false)

  // Memoize the blur data URL generation to avoid unnecessary fetches
  const generateBlurDataURL = useCallback(async (imageSrc) => {
    if (!imageSrc) return

    try {
      const response = await fetch(`${imageSrc}?w=10&q=30`) // Lower quality for faster loading
      if (!response.ok) throw new Error('Failed to fetch blur image')

      const blob = await response.blob()

      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      console.warn('Failed to generate blur placeholder:', error)
      return null
    }
  }, [])

  useEffect(() => {
    if (!src) return

    let isMounted = true

    generateBlurDataURL(src).then((base64) => {
      if (isMounted && base64) {
        setBase64BlurDataURL(base64)
      }
    })

    return () => {
      isMounted = false
    }
  }, [src, generateBlurDataURL])

  // Handle image load errors
  const handleImageError = () => {
    setImageError(true)
  }

  // Don't render if we have an error and no fallback
  if (imageError && !props.style?.backgroundColor) {
    return (
      <div
        style={{
          backgroundColor: '#f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          ...props.style
        }}
      >
        <span style={{ color: '#6b7280', fontSize: '14px' }}>{alt || 'Image not available'}</span>
      </div>
    )
  }

  const avifSrc = `${src}?fit=thumb&fm=avif`
  const defaultSrc = `${src}?fit=thumb&fm=avif`

  if (width && height) {
    return (
      <picture className="c-lazy-image">
        <source srcSet={`${avifSrc}&w=600`} media="(max-width: 600px)" />
        <source srcSet={`${avifSrc}&w=1200`} media="(max-width: 1200px)" />
        <Image
          src={defaultSrc}
          alt={alt || 'Article image'}
          width={width}
          height={height}
          sizes={sizes}
          placeholder={base64BlurDataURL ? 'blur' : 'empty'}
          blurDataURL={base64BlurDataURL}
          onError={handleImageError}
          style={{
            width: '100%',
            height: 'auto',
            objectFit: 'contain'
          }}
          {...props}
        />
      </picture>
    )
  }

  return (
    <picture className="c-lazy-image">
      <source srcSet={`${avifSrc}&w=600`} media="(max-width: 600px)" />
      <source srcSet={`${avifSrc}&w=1200`} media="(max-width: 1200px)" />
      <Image
        src={`${src}?fm=avif`}
        alt={alt || 'Article image'}
        sizes="(max-width: 600px) 100vw, 50vw"
        placeholder={base64BlurDataURL ? 'blur' : 'empty'}
        blurDataURL={base64BlurDataURL}
        onError={handleImageError}
        {...props}
      />
    </picture>
  )
}

export default LazyImage
